// ═══════════════════════════════════════════════════════════════════
//  indexEntities — Walk workflows + atlas → SearchableEntity[].
// ═══════════════════════════════════════════════════════════════════
//
//  Two entry points:
//    indexWorkflow(row)   ── one DB workflow row (single- or multi-sheet)
//    indexAtlas(atlas)    ── an Atlas manifest
//
//  Both return a flat list of SearchableEntity. They tolerate compact
//  AND full formats. The walker matches the field-key vocabulary used
//  by mcp-server-flowweaver/src/deep-search.ts so AI hits and UI hits
//  rank consistently.
//
// ═══════════════════════════════════════════════════════════════════

import type { Atlas } from '@/types/atlas';
import type { SearchableEntity } from '@/types/search';

/**
 * Field-key → display weight. Higher means a match in this field
 * makes the hit more relevant. Mirrors the MCP server's table.
 */
export const FIELD_WEIGHTS: Record<string, number> = {
  name: 100,
  n: 100,
  title: 100,
  label: 80,
  description: 60,
  d: 60,
  detailedDescription: 60,
  dd: 60,
  subject: 50,
  to: 50,
  webhookUrl: 50,
  linkTarget: 50,
  icon: 50,
  tag: 40,
  tags: 40,
  type: 30,
  t: 30,
  category: 25,
  id: 20,
  i: 20,
};

export function fieldWeight(key: string): number {
  return FIELD_WEIGHTS[key] ?? 10;
}

// ────────────────────────────────────────────────────────────────────
//  Workflow indexer
// ────────────────────────────────────────────────────────────────────

interface WorkflowRow {
  id: string;
  title?: string;
  workflow_name?: string;
  category?: string;
  blocks?: unknown;
  connectors?: unknown;
  groups?: unknown;
  [key: string]: unknown;
}

interface MultiSheetWrapper {
  _multiSheet: true;
  data: {
    sheets: Array<MultiSheetSheet>;
    crossSheetConnectors?: Array<Record<string, unknown>>;
    activeSheetIndex?: number;
  };
}

interface MultiSheetSheet {
  id?: string;
  name?: string;
  blocks?: unknown[];
  connectors?: unknown[];
  groups?: unknown[];
}

/** Flatten a workflow row into searchable entities. */
export function indexWorkflow(row: WorkflowRow): SearchableEntity[] {
  const out: SearchableEntity[] = [];
  const wfTitle = row.title ?? row.workflow_name ?? `Workflow ${row.id}`;

  // workflow-meta entity (so users searching for the workflow's own
  // name, category, etc. surface the workflow itself)
  out.push({
    id: `wf:${row.id}`,
    sourceType: 'workflow-meta',
    sourceId: row.id,
    parentLabel: 'Workflow',
    entityLabel: wfTitle,
    fields: {
      title: wfTitle,
      ...(row.category ? { category: String(row.category) } : {}),
      id: row.id,
    },
    score: 100,
    openUrl: `/flow-weaver?workflow=${encodeURIComponent(row.id)}`,
  });

  const blocks = row.blocks;
  if (isMultiSheetWrapper(blocks)) {
    const data = blocks.data;
    data.sheets.forEach((sh, idx) => {
      indexSheet(out, row.id, wfTitle, sh, idx);
    });
    for (const csc of data.crossSheetConnectors ?? []) {
      indexCrossSheetConnector(out, row.id, wfTitle, csc);
    }
  } else {
    // Single-sheet — treat row.blocks/connectors/groups as one sheet.
    indexSheet(
      out,
      row.id,
      wfTitle,
      {
        id: 'sheet-0',
        name: wfTitle,
        blocks: Array.isArray(blocks) ? blocks : [],
        connectors: Array.isArray(row.connectors) ? row.connectors : [],
        groups: Array.isArray(row.groups) ? row.groups : [],
      },
      0,
    );
  }
  return out;
}

function isMultiSheetWrapper(b: unknown): b is MultiSheetWrapper {
  return (
    !!b &&
    typeof b === 'object' &&
    !Array.isArray(b) &&
    (b as MultiSheetWrapper)._multiSheet === true
  );
}

function indexSheet(
  out: SearchableEntity[],
  workflowId: string,
  workflowTitle: string,
  sheet: MultiSheetSheet,
  sheetIndex: number,
): void {
  const sheetId = sheet.id ?? `sheet-${sheetIndex}`;
  const sheetName = sheet.name ?? `Sheet ${sheetIndex + 1}`;

  // workflow-sheet entity (only emit for multi-sheet so we don't
  // duplicate workflow-meta on single-sheet workflows).
  if (sheetIndex > 0 || sheet.id) {
    out.push({
      id: `wf:${workflowId}/sheet:${sheetId}`,
      sourceType: 'workflow-sheet',
      sourceId: workflowId,
      parentLabel: workflowTitle,
      entityLabel: sheetName,
      fields: { name: sheetName, id: sheetId },
      score: 60,
      openUrl: `/flow-weaver?workflow=${encodeURIComponent(workflowId)}&sheet=${encodeURIComponent(sheetId)}`,
    });
  }

  // Blocks
  for (const block of sheet.blocks ?? []) {
    if (block && typeof block === 'object') {
      indexBlock(
        out,
        workflowId,
        workflowTitle,
        sheetId,
        sheetName,
        block as Record<string, unknown>,
      );
    }
  }

  // Connectors
  for (const conn of sheet.connectors ?? []) {
    if (conn && typeof conn === 'object') {
      indexConnector(
        out,
        workflowId,
        workflowTitle,
        sheetId,
        sheetName,
        conn as Record<string, unknown>,
      );
    }
  }

  // Groups
  for (const group of sheet.groups ?? []) {
    if (group && typeof group === 'object') {
      indexGroup(
        out,
        workflowId,
        workflowTitle,
        sheetId,
        sheetName,
        group as Record<string, unknown>,
      );
    }
  }
}

function indexBlock(
  out: SearchableEntity[],
  workflowId: string,
  workflowTitle: string,
  sheetId: string,
  sheetName: string,
  block: Record<string, unknown>,
): void {
  const blockId = (block.id ?? block.i ?? '') as string;
  if (!blockId) return;
  const fields = extractFields(block);
  // Block.config holds the bulk of searchable strings — flatten one
  // level so config.subject becomes "subject" in the hits.
  if (block.config && typeof block.config === 'object') {
    Object.assign(fields, extractFields(block.config as Record<string, unknown>));
  }
  const entityLabel =
    typeof block.name === 'string'
      ? block.name
      : typeof block.n === 'string'
        ? block.n
        : typeof (block.config as Record<string, unknown>)?.name === 'string'
          ? ((block.config as Record<string, unknown>).name as string)
          : `Block ${blockId.slice(0, 6)}`;
  out.push({
    id: `wf:${workflowId}/sheet:${sheetId}/block:${blockId}`,
    sourceType: 'workflow-block',
    sourceId: workflowId,
    parentLabel: `${workflowTitle} · ${sheetName}`,
    entityLabel,
    fields,
    score: 100,
    openUrl: `/flow-weaver?workflow=${encodeURIComponent(workflowId)}&sheet=${encodeURIComponent(sheetId)}&focus=${encodeURIComponent(blockId)}`,
  });
}

function indexConnector(
  out: SearchableEntity[],
  workflowId: string,
  workflowTitle: string,
  sheetId: string,
  sheetName: string,
  conn: Record<string, unknown>,
): void {
  const connId = (conn.id ?? '') as string;
  if (!connId) return;
  const fields = extractFields(conn);
  const entityLabel =
    typeof conn.label === 'string' && conn.label
      ? conn.label
      : `Connector ${connId.slice(0, 6)}`;
  out.push({
    id: `wf:${workflowId}/sheet:${sheetId}/conn:${connId}`,
    sourceType: 'workflow-connector',
    sourceId: workflowId,
    parentLabel: `${workflowTitle} · ${sheetName}`,
    entityLabel,
    fields,
    score: 30,
    openUrl: `/flow-weaver?workflow=${encodeURIComponent(workflowId)}&sheet=${encodeURIComponent(sheetId)}&focusConn=${encodeURIComponent(connId)}`,
  });
}

function indexGroup(
  out: SearchableEntity[],
  workflowId: string,
  workflowTitle: string,
  sheetId: string,
  sheetName: string,
  group: Record<string, unknown>,
): void {
  const groupId = (group.id ?? '') as string;
  if (!groupId) return;
  const fields = extractFields(group);
  const entityLabel =
    typeof group.label === 'string' && group.label
      ? group.label
      : `Group ${groupId.slice(0, 6)}`;
  out.push({
    id: `wf:${workflowId}/sheet:${sheetId}/group:${groupId}`,
    sourceType: 'workflow-group',
    sourceId: workflowId,
    parentLabel: `${workflowTitle} · ${sheetName}`,
    entityLabel,
    fields,
    score: 60,
    openUrl: `/flow-weaver?workflow=${encodeURIComponent(workflowId)}&sheet=${encodeURIComponent(sheetId)}&focusGroup=${encodeURIComponent(groupId)}`,
  });
}

function indexCrossSheetConnector(
  out: SearchableEntity[],
  workflowId: string,
  workflowTitle: string,
  csc: Record<string, unknown>,
): void {
  const cscId = (csc.id ?? '') as string;
  if (!cscId) return;
  const fields = extractFields(csc);
  const entityLabel =
    typeof csc.label === 'string' && csc.label
      ? csc.label
      : `Cross-sheet connector ${cscId.slice(0, 6)}`;
  out.push({
    id: `wf:${workflowId}/csc:${cscId}`,
    sourceType: 'workflow-cross-sheet-connector',
    sourceId: workflowId,
    parentLabel: workflowTitle,
    entityLabel,
    fields,
    score: 40,
    openUrl: `/flow-weaver?workflow=${encodeURIComponent(workflowId)}&focusCsc=${encodeURIComponent(cscId)}`,
  });
}

// ────────────────────────────────────────────────────────────────────
//  Atlas indexer
// ────────────────────────────────────────────────────────────────────

export function indexAtlas(atlas: Atlas): SearchableEntity[] {
  const out: SearchableEntity[] = [];
  const atlasId = atlas.id;

  // Categories
  for (const cat of atlas.categories ?? []) {
    out.push({
      id: `atlas:${atlasId}/cat:${cat.id}`,
      sourceType: 'atlas-category',
      sourceId: atlasId,
      parentLabel: `Atlas: ${atlas.name}`,
      entityLabel: cat.label,
      fields: extractFields(cat as unknown as Record<string, unknown>),
      score: 40,
      openUrl: `/atlas?cat=${encodeURIComponent(cat.id)}`,
    });
  }

  // Documents + their sheets
  for (const doc of atlas.documents ?? []) {
    out.push({
      id: `atlas:${atlasId}/doc:${doc.id}`,
      sourceType: 'atlas-doc',
      sourceId: atlasId,
      parentLabel: `Atlas: ${atlas.name}`,
      entityLabel: doc.title,
      fields: {
        ...extractFields(doc as unknown as Record<string, unknown>),
        ...(Array.isArray(doc.tags) ? { tags: doc.tags.join(' ') } : {}),
      },
      score: 80,
      openUrl: `/atlas/doc/${encodeURIComponent(doc.id)}`,
    });
    for (const sheet of doc.sheets ?? []) {
      out.push({
        id: `atlas:${atlasId}/doc:${doc.id}/sheet:${sheet.id}`,
        sourceType: 'atlas-sheet',
        sourceId: atlasId,
        parentLabel: `Atlas: ${atlas.name} · ${doc.title}`,
        entityLabel: sheet.title,
        fields: extractFields(sheet as unknown as Record<string, unknown>),
        score: 50,
        openUrl: `/atlas/doc/${encodeURIComponent(doc.id)}/sheet/${encodeURIComponent(sheet.id)}`,
      });
    }
  }

  // Cross-document refs
  for (const ref of atlas.crossDocumentRefs ?? []) {
    const fromDoc = atlas.documents.find((d) => d.id === ref.fromDocumentId);
    const toDoc = atlas.documents.find((d) => d.id === ref.toDocumentId);
    const label =
      ref.label ??
      `${fromDoc?.title ?? ref.fromDocumentId} → ${toDoc?.title ?? ref.toDocumentId}`;
    out.push({
      id: `atlas:${atlasId}/ref:${ref.id}`,
      sourceType: 'atlas-ref',
      sourceId: atlasId,
      parentLabel: `Atlas: ${atlas.name}`,
      entityLabel: label,
      fields: extractFields(ref as unknown as Record<string, unknown>),
      score: 70,
      openUrl: `/atlas/doc/${encodeURIComponent(ref.fromDocumentId)}#ref:${encodeURIComponent(ref.id)}`,
    });
  }

  return out;
}

// ────────────────────────────────────────────────────────────────────
//  Field extractor
// ────────────────────────────────────────────────────────────────────

/**
 * Pull every string-valued field out of an object, one level deep.
 * Skips nested arrays/objects (those are walked separately by the
 * indexers above when they have known shape, e.g. block.config).
 */
export function extractFields(obj: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'string' && v.length > 0) {
      out[k] = v;
    } else if (typeof v === 'number' && Number.isFinite(v)) {
      // Skip pure-coordinate fields — too noisy.
      if (k === 'x' || k === 'y' || k === 'width' || k === 'height') continue;
      out[k] = String(v);
    } else if (Array.isArray(v) && v.every((item) => typeof item === 'string')) {
      // Tags-like arrays — flatten to space-joined string for searching.
      out[k] = (v as string[]).join(' ');
    }
  }
  return out;
}
